<!-- ═══ Product detail dialog ════════════════════════════════════════════════ -->
<p-dialog
  [visible]="detailVisible()"
  (visibleChange)="detailVisible.set($event)"
  (onHide)="detailProduct.set(null)"
  [modal]="true"
  [header]="detailProduct()?.name ?? ''"
  [style]="{ width: 'min(56rem, 95vw)' }"
  [contentStyle]="{ 'max-height': '82vh', 'overflow-y': 'auto' }"
  [draggable]="false"
  [resizable]="false"
>
  @if (detailProduct(); as product) {
    <app-product-detail
      [product]="product"
      [storeId]="storeCtx.selectedStoreId()"
      mode="sale"
      (addToCart)="onDetailAddToCart($event)"
    />
  }

  <ng-template pTemplate="footer">
    <p-button
      [label]="'COMMON.CLOSE' | translate"
      severity="secondary"
      [outlined]="true"
      (onClick)="detailVisible.set(false)"
    />
  </ng-template>
</p-dialog>

<!--
  ═══ Barcode scanner ═══════════════════════════════════════════════════════
  Tugmasiz: sarlavhadagi skaner tugmasi to'g'ridan-to'g'ri `startScan()` ni
  chaqiradi. Avval bu dialog ichida edi va kassir kamerani ochish uchun ikkinchi
  tugmani ham bosishi kerak bo'lardi. Skanerning o'zi butun ekranni egallovchi
  overlay chizadi, shuning uchun o'rab turuvchi dialog ortiqcha.
-->
<app-barcode-scanner #scanner [showTrigger]="false" (barcodeDetected)="onBarcodeDetected($event)" />

<!-- ═══ Variant picker ══════════════════════════════════════════════════════ -->
<app-variant-picker
  [visible]="pickerVisible()"
  (visibleChange)="pickerVisible.set($event)"
  [productId]="pickerProduct()?.id ?? null"
  [storeId]="storeCtx.selectedStoreId()"
  [sellCurrency]="pickerProduct()?.sellCurrency ?? null"
  (variantSelected)="onVariantSelected($event)"
/>

<!-- ═══ Cart item edit overlay ═════════════════════════════════════════════ -->
<p-dialog
  [visible]="!!qtyEditItem()"
  (visibleChange)="!$event && qtyEditItem.set(null)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="w-full max-w-md"
  [contentStyle]="{ 'overflow-y': 'auto', 'scrollbar-width': 'none', padding: '16px' }"
  [header]="qtyEditItem()?.productName ?? ('SALES.QTY_EDIT' | translate)"
>
  @if (qtyEditItem(); as item) {
    <div class="flex flex-col gap-3">
      <!-- Mode toggle -->
      <div
        class="flex rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 gap-0.5 p-0.5 bg-gray-100 dark:bg-gray-800 text-sm font-semibold"
      >
        <button
          type="button"
          (click)="switchEditMode('price')"
          class="flex-1 py-2.5 rounded-lg transition-all"
          [class.bg-white]="editMode() === 'price'"
          [class.dark:bg-gray-900]="editMode() === 'price'"
          [class.text-emerald-600]="editMode() === 'price'"
          [class.shadow-sm]="editMode() === 'price'"
          [class.text-gray-500]="editMode() !== 'price'"
        >
          {{ 'SALES.MODE_AMOUNT' | translate }}
        </button>
        <button
          type="button"
          (click)="switchEditMode('qty')"
          class="flex-1 py-2.5 rounded-lg transition-all"
          [class.bg-white]="editMode() === 'qty'"
          [class.dark:bg-gray-900]="editMode() === 'qty'"
          [class.text-emerald-600]="editMode() === 'qty'"
          [class.shadow-sm]="editMode() === 'qty'"
          [class.text-gray-500]="editMode() !== 'qty'"
        >
          {{ 'SALES.MODE_QTY' | translate }}
        </button>
      </div>

      <!-- Main input -->
      <div class="relative">
        <input
          type="text"
          inputmode="decimal"
          [value]="editMainInput()"
          (focus)="onEditFocus($event, 'main')"
          (input)="onEditInput($event, 'main')"
          (keydown)="onEditKeydown($event)"
          class="w-full text-right px-4 py-3 pr-16 rounded-xl border-2 transition-colors tabular-nums font-bold text-3xl leading-none outline-none"
          [class.border-emerald-500]="editActiveField() === 'main'"
          [class.bg-emerald-50]="editActiveField() === 'main'"
          [class.dark:bg-emerald-900/20]="editActiveField() === 'main'"
          [class.text-emerald-700]="editActiveField() === 'main'"
          [class.dark:text-emerald-300]="editActiveField() === 'main'"
          [class.border-gray-200]="editActiveField() !== 'main'"
          [class.dark:border-gray-700]="editActiveField() !== 'main'"
          [class.bg-white]="editActiveField() !== 'main'"
          [class.dark:bg-gray-900]="editActiveField() !== 'main'"
          [class.text-gray-900]="editActiveField() !== 'main'"
          [class.dark:text-gray-100]="editActiveField() !== 'main'"
        />
        <span
          class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"
        >
          {{ editMode() === 'price' ? symbolOf(item.currency) : ('COMMON.UNIT_PIECE' | translate) }}
        </span>
      </div>

      <!-- Summary preview -->
      <div class="rounded-xl bg-gray-50 dark:bg-gray-800/60 px-4 py-3 space-y-1.5 text-sm flex-1">
        @if (editMode() === 'price') {
          <div class="flex justify-between text-gray-500">
            <span>{{ 'SALES.APPROX_QTY' | translate }}</span>
            <span class="tabular-nums font-semibold"
              >{{ editCalcQty() | number: '1.0-6' }} {{ 'COMMON.UNIT_PIECE' | translate }}</span
            >
          </div>
        } @else {
          <div class="flex justify-between text-gray-500">
            <span class="tabular-nums"
              >{{ editMainInput() }} × {{ item.unitPrice | money: item.currency }}</span
            >
            <span class="tabular-nums">{{
              editMainVal() * item.unitPrice | money: item.currency
            }}</span>
          </div>
          @if (
            editDiscountOpen() &&
            editDiscVal() > 0 &&
            editCalcSubtotal() < editMainVal() * item.unitPrice
          ) {
            <div class="flex justify-between text-rose-500">
              <span>{{ 'SALES.DISCOUNT_COLON' | translate }}</span>
              <span class="tabular-nums"
                >-{{
                  editMainVal() * item.unitPrice - editCalcSubtotal() | money: item.currency
                }}</span
              >
            </div>
          }
        }
        <div
          class="flex justify-between font-semibold text-gray-900 dark:text-gray-100 pt-1.5 border-t border-gray-200 dark:border-gray-700"
        >
          <span>{{ 'SALES.TOTAL_COLON' | translate }}</span>
          <span class="tabular-nums text-emerald-600 dark:text-emerald-400">{{
            editCalcSubtotal() | money: item.currency
          }}</span>
        </div>
      </div>

      <!-- Discount section (qty mode only) -->
      @if (editMode() === 'qty') {
        <div class="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            type="button"
            (click)="toggleEditDiscount()"
            class="w-full flex items-center justify-between px-4 py-2.5 text-sm font-semibold bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
          >
            <div class="flex items-center gap-2">
              <lucide-icon [img]="Tag" size="15" />
              <span>{{ 'SALES.DISCOUNT' | translate }}</span>
            </div>
            <span
              [class.text-emerald-500]="editDiscountOpen()"
              [class.text-gray-400]="!editDiscountOpen()"
            >
              {{
                editDiscountOpen() ? ('SALES.DISCOUNT_ON' | translate) : ('COMMON.ADD' | translate)
              }}
            </span>
          </button>
          @if (editDiscountOpen()) {
            <div class="px-4 pb-4 pt-3 bg-white dark:bg-gray-900 space-y-2.5">
              <div class="flex gap-2">
                <button
                  type="button"
                  (click)="editDiscountType.set('pct')"
                  class="flex-1 py-2 text-sm rounded-xl border-2 font-bold transition-all"
                  [class.border-emerald-400]="editDiscountType() === 'pct'"
                  [class.bg-emerald-50]="editDiscountType() === 'pct'"
                  [class.text-emerald-700]="editDiscountType() === 'pct'"
                  [class.border-gray-200]="editDiscountType() !== 'pct'"
                  [class.dark:border-gray-700]="editDiscountType() !== 'pct'"
                  [class.text-gray-500]="editDiscountType() !== 'pct'"
                >
                  %
                </button>
                <button
                  type="button"
                  (click)="editDiscountType.set('fixed')"
                  class="flex-1 py-2 text-sm rounded-xl border-2 font-bold transition-all"
                  [class.border-emerald-400]="editDiscountType() === 'fixed'"
                  [class.bg-emerald-50]="editDiscountType() === 'fixed'"
                  [class.text-emerald-700]="editDiscountType() === 'fixed'"
                  [class.border-gray-200]="editDiscountType() !== 'fixed'"
                  [class.dark:border-gray-700]="editDiscountType() !== 'fixed'"
                  [class.text-gray-500]="editDiscountType() !== 'fixed'"
                >
                  {{ symbolOf(item.currency) }}
                </button>
              </div>
              <div class="relative">
                <input
                  type="text"
                  inputmode="decimal"
                  [value]="editDiscountInput()"
                  (focus)="onEditFocus($event, 'discount')"
                  (input)="onEditInput($event, 'discount')"
                  (keydown)="onEditKeydown($event)"
                  class="w-full text-right px-4 py-2.5 pr-14 rounded-xl border-2 transition-colors tabular-nums font-bold text-xl outline-none"
                  [class.border-emerald-500]="editActiveField() === 'discount'"
                  [class.bg-emerald-50]="editActiveField() === 'discount'"
                  [class.text-emerald-700]="editActiveField() === 'discount'"
                  [class.border-gray-200]="editActiveField() !== 'discount'"
                  [class.dark:border-gray-700]="editActiveField() !== 'discount'"
                  [class.bg-white]="editActiveField() !== 'discount'"
                  [class.dark:bg-gray-900]="editActiveField() !== 'discount'"
                  [class.text-gray-800]="editActiveField() !== 'discount'"
                  [class.dark:text-gray-200]="editActiveField() !== 'discount'"
                />
                <span
                  class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400"
                >
                  {{ editDiscountType() === 'pct' ? '%' : symbolOf(item.currency) }}
                </span>
              </div>
            </div>
          }
        </div>
      }
    </div>
  }

  <ng-template pTemplate="footer">
    <div class="flex gap-3">
      <p-button
        [label]="'COMMON.CANCEL' | translate"
        severity="secondary"
        styleClass="flex-1"
        (onClick)="qtyEditItem.set(null)"
      />
      <p-button
        [label]="'COMMON.SAVE' | translate"
        styleClass="flex-1"
        (onClick)="confirmQtyEdit()"
      />
    </div>
  </ng-template>
</p-dialog>

<!-- ═══ Payment dialog ══════════════════════════════════════════════════════ -->
<p-dialog
  [visible]="paymentVisible()"
  (visibleChange)="paymentVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="w-full max-w-md"
  [header]="'SALES.PAYMENT.TITLE' | translate"
>
  <div class="space-y-5 pt-1">
    <!-- Payment type -->
    <div>
      <p
        class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2"
      >
        {{ 'SALES.PAYMENT.TYPE' | translate }}
      </p>
      <div class="grid grid-cols-3 gap-2">
        @for (opt of paymentTypeOptions; track opt.value) {
          <button
            type="button"
            (click)="onPaymentTypeChange(opt.value)"
            class="py-3 rounded-xl border-2 text-sm font-semibold transition-all text-center"
            [class.border-emerald-500]="paymentType() === opt.value"
            [class.bg-emerald-50]="paymentType() === opt.value"
            [class.text-emerald-700]="paymentType() === opt.value"
            [class.dark:bg-emerald-900/30]="paymentType() === opt.value"
            [class.dark:text-emerald-300]="paymentType() === opt.value"
            [class.border-gray-200]="paymentType() !== opt.value"
            [class.dark:border-gray-700]="paymentType() !== opt.value"
            [class.text-gray-600]="paymentType() !== opt.value"
            [class.dark:text-gray-400]="paymentType() !== opt.value"
          >
            {{ opt.label | translate }}
          </button>
        }
      </div>
    </div>
    <!-- Summary -->
    <div class="rounded-xl bg-gray-50 dark:bg-gray-800/60 p-4 space-y-2">
      <div class="flex justify-between text-sm text-gray-500 dark:text-gray-400">
        <span>{{ 'SALES.SUBTOTAL' | translate }}</span>
        <span class="tabular-nums">
          @for (g of cartTotalsByCurrency(); track g.currency) {
            <span class="block">{{ g.subtotal | money: g.currency }}</span>
          }
        </span>
      </div>
      @if (cartDiscount() > 0) {
        <div class="flex justify-between text-sm text-rose-500">
          <span>{{ 'SALES.DISCOUNT' | translate }}</span>
          <span class="tabular-nums"
            >-
            @for (g of cartTotalsByCurrency(); track g.currency) {
              @if (g.discount > 0) {
                <span class="block">{{ g.discount | money: g.currency }}</span>
              }
            }
          </span>
        </div>
      }
      <div
        class="flex justify-between font-bold text-base text-gray-900 dark:text-gray-100 pt-1 border-t border-gray-200 dark:border-gray-700"
      >
        <span>{{ 'SALES.TOTAL' | translate }}</span>
        <span class="tabular-nums">
          @for (g of cartTotalsByCurrency(); track g.currency) {
            <span class="block">{{ g.total | money: g.currency }}</span>
          }
        </span>
      </div>
    </div>
    <!--
      To'lov valyutasi. Order o'z valyutasida qoladi (masalan `$100`), mijoz esa
      boshqa pulda to'lashi mumkin — ekranda ekvivalent va kurs ko'rsatiladi.
      Yakuniy summani kassir belgilaydi (I13), tizim faqat TAKLIF qiladi.
    -->
    @if (currencySvc.hasMultipleCurrencies()) {
      <div class="space-y-3">
        <!--
          Savat bir nechta valyutali bo'lsa mijoz tanlashi mumkin: hammasini bitta
          pulda berish, yoki har molni o'z pulida. Ikkinchisida kurs kerak emas.
        -->
        @if (isMixedCart()) {
          <div class="flex gap-2">
            <button
              type="button"
              (click)="setPayPerCurrency(false)"
              class="flex-1 py-2 px-3 text-sm rounded-xl border-2 font-semibold transition-all"
              [class.border-emerald-400]="!payPerCurrency()"
              [class.bg-emerald-50]="!payPerCurrency()"
              [class.dark:bg-emerald-900/20]="!payPerCurrency()"
              [class.text-emerald-700]="!payPerCurrency()"
              [class.dark:text-emerald-300]="!payPerCurrency()"
              [class.border-gray-200]="payPerCurrency()"
              [class.dark:border-gray-700]="payPerCurrency()"
              [class.text-gray-500]="payPerCurrency()"
            >
              {{ 'SALES.PAYMENT.MODE_SINGLE' | translate }}
            </button>
            <button
              type="button"
              (click)="setPayPerCurrency(true)"
              class="flex-1 py-2 px-3 text-sm rounded-xl border-2 font-semibold transition-all"
              [class.border-emerald-400]="payPerCurrency()"
              [class.bg-emerald-50]="payPerCurrency()"
              [class.dark:bg-emerald-900/20]="payPerCurrency()"
              [class.text-emerald-700]="payPerCurrency()"
              [class.dark:text-emerald-300]="payPerCurrency()"
              [class.border-gray-200]="!payPerCurrency()"
              [class.dark:border-gray-700]="!payPerCurrency()"
              [class.text-gray-500]="!payPerCurrency()"
            >
              {{ 'SALES.PAYMENT.MODE_PER_CURRENCY' | translate }}
            </button>
          </div>
        }

        @if (!payPerCurrency()) {
          <div>
            <label
              for="pay-currency"
              class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block"
            >
              {{ 'SALES.PAYMENT.CURRENCY' | translate }}
            </label>
            <p-select
              inputId="pay-currency"
              [options]="currencySvc.currencyOptions()"
              optionLabel="label"
              optionValue="value"
              [ngModel]="payCurrency()"
              (ngModelChange)="onPayCurrencyChange($event)"
              appendTo="body"
              class="w-full"
            />
          </div>

          @if (isCrossCurrency()) {
            <!--
              Kurs TAHRIRLANADI: sozlamadagi kurs faqat taklif, kassir mijoz bilan
              boshqasiga kelishishi mumkin (I13). Kelishilgan kurs faqat SHU savdoga
              tegishli — sozlamalarga yozilmaydi.
            -->
            <div
              class="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800"
            >
              @for (g of rateRows(); track g.currency) {
                <div class="p-3 space-y-2">
                  <div class="flex items-center justify-between gap-2">
                    <span class="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {{ 'SALES.PAYMENT.RATE_FOR' | translate: { currency: g.currency } }}
                    </span>
                    @if (isRateAgreed(g.currency)) {
                      <button
                        type="button"
                        (click)="clearSaleRate(g.currency)"
                        class="text-[11px] font-semibold text-emerald-600 hover:underline"
                      >
                        {{ 'SALES.PAYMENT.RATE_RESET' | translate }}
                      </button>
                    }
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-500 whitespace-nowrap"
                      >1 {{ g.currency }} =</span
                    >
                    <input
                      pInputText
                      type="number"
                      min="0"
                      step="any"
                      [value]="effectiveRate(g.currency)"
                      (input)="setSaleRate(g.currency, +$any($event.target).value)"
                      class="flex-1 min-w-0 text-right font-semibold tabular-nums"
                      [class.border-amber-400]="isRateAgreed(g.currency)"
                    />
                    <span class="text-sm text-gray-500">{{ payCurrency() }}</span>
                  </div>
                  <!-- Mijoz shu mol uchun qancha berishini DARHOL ko'rsatadi. -->
                  <div class="flex items-center justify-between text-sm">
                    <span class="tabular-nums text-gray-500">{{
                      g.total | money: g.currency
                    }}</span>
                    <span class="tabular-nums font-bold text-gray-900 dark:text-gray-100">
                      = {{ convertedTotal(g) | money: payCurrency() }}
                    </span>
                  </div>
                  @if (isRateAgreed(g.currency) && settingsRate(g.currency) !== null) {
                    <p class="text-[11px] text-amber-600 dark:text-amber-400">
                      {{
                        'SALES.PAYMENT.RATE_AGREED_HINT'
                          | translate: { rate: settingsRate(g.currency) }
                      }}
                    </p>
                  }
                </div>
              }
            </div>

            @if (rateMissing()) {
              <p class="text-sm text-red-600 dark:text-red-400 font-medium">
                {{
                  'SALES.PAYMENT.RATE_MISSING'
                    | translate: { from: missingRateFrom(), to: payCurrency() }
                }}
              </p>
            } @else {
              <div
                class="flex items-center justify-between rounded-xl bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2.5"
              >
                <span class="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                  {{ 'SALES.PAYMENT.TOTAL_IN_PAY_CURRENCY' | translate }}
                </span>
                <span
                  class="text-base font-bold tabular-nums text-emerald-700 dark:text-emerald-300"
                >
                  {{ cartTotalInPayCurrency() | money: payCurrency() }}
                </span>
              </div>
            }
          }
        }
      </div>
    }

    <!-- Cash: paid amount -->
    @if (paymentType() === 0) {
      @if (payPerCurrency()) {
        <!-- Har valyuta alohida olinadi va alohida qaytim beriladi. -->
        <div class="space-y-3">
          @for (g of cartTotalsByCurrency(); track g.currency) {
            <div>
              <label
                [attr.for]="'paid-' + g.currency"
                class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex justify-between"
              >
                <span>{{ 'SALES.PAYMENT.PAID_AMOUNT' | translate }} — {{ g.currency }}</span>
                <span class="tabular-nums normal-case text-gray-400">
                  {{ g.total | money: g.currency }}
                </span>
              </label>
              <input
                pInputText
                [id]="'paid-' + g.currency"
                type="number"
                min="0"
                [value]="paidFor(g.currency)"
                (input)="setPaidFor(g.currency, +$any($event.target).value)"
                class="w-full text-lg font-semibold"
                [class.border-red-400]="paidFor(g.currency) < g.total"
              />
              @if (changeFor(g.currency) > 0) {
                <p class="mt-1 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                  {{ 'SALES.PAYMENT.CHANGE' | translate }}:
                  <span class="font-bold tabular-nums">{{
                    changeFor(g.currency) | money: g.currency
                  }}</span>
                </p>
              }
            </div>
          }
        </div>
      } @else {
        <div>
          <label
            for=""
            class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block"
          >
            {{ 'SALES.PAYMENT.PAID_AMOUNT' | translate }}
          </label>
          <input
            pInputText
            type="number"
            min="0"
            [value]="paidAmount()"
            (input)="paidAmount.set(+$any($event.target).value)"
            class="w-full text-lg font-semibold"
          />
          @if (change() > 0) {
            <p class="mt-1.5 text-sm text-emerald-600 dark:text-emerald-400 font-medium">
              {{ 'SALES.PAYMENT.CHANGE' | translate }}:
              <span class="font-bold tabular-nums">{{ change() | money: payCurrency() }}</span>
            </p>
          }
        </div>
      }
    }
    <!-- Debt: customer info -->
    @if (paymentType() === 2) {
      <div class="space-y-3">
        @if (payPerCurrency()) {
          <!--
            Har valyuta o'z pulida: oldindan to'lov ham alohida olinadi va qarz
            ham har valyutada alohida qoladi (I9).
          -->
          @for (g of cartTotalsByCurrency(); track g.currency) {
            <div>
              <label
                [attr.for]="'prepaid-' + g.currency"
                class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 flex justify-between"
              >
                <span>{{ 'SALES.PAYMENT.PREPAID' | translate }} — {{ g.currency }}</span>
                <span class="tabular-nums normal-case text-gray-400">
                  {{ g.total | money: g.currency }}
                </span>
              </label>
              <input
                pInputText
                [id]="'prepaid-' + g.currency"
                type="number"
                min="0"
                [max]="g.total"
                [value]="paidFor(g.currency)"
                (input)="setPaidFor(g.currency, +$any($event.target).value)"
                class="w-full"
              />
              <p class="mt-1 text-xs text-gray-400">
                {{ 'SALES.PAYMENT.REMAINING_DEBT' | translate }}:
                <span class="font-semibold text-rose-500 tabular-nums">{{
                  debtFor(g.currency) | money: g.currency
                }}</span>
              </p>
            </div>
          }
        } @else {
          <!-- Oldindan to'lov (ixtiyoriy) -->
          <div>
            <label
              for=""
              class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block"
            >
              {{ 'SALES.PAYMENT.PREPAID' | translate }}
            </label>
            <input
              pInputText
              type="number"
              min="0"
              [max]="dueInPayCurrency()"
              [value]="paidAmount()"
              (input)="paidAmount.set(+$any($event.target).value)"
              class="w-full"
            />
            <p class="mt-1 text-xs text-gray-400">
              <!-- Oldindan to'lov mijoz bergan valyutada — qoldiq ham shunda. -->
              {{ 'SALES.PAYMENT.REMAINING_DEBT' | translate }}:
              <span class="font-semibold text-rose-500 tabular-nums">{{
                dueInPayCurrency() - paidAmount() | money: payCurrency()
              }}</span>
            </p>
          </div>
        }
        <!-- Oldindan to'lov QANDAY berilgani — naqd/karta/bank moslashsa pul to'g'ri hisobga tushadi. -->
        @if (hasAnyPrepaid()) {
          <div>
            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
              {{ 'SALES.PAYMENT.PREPAID_METHOD' | translate }}
            </p>
            <div class="grid grid-cols-3 gap-2">
              @for (opt of prepaidMethodOptions; track opt.value) {
                <button
                  type="button"
                  (click)="prepaidMethod.set(opt.value)"
                  class="py-2 rounded-lg border-2 text-sm font-semibold transition-all text-center"
                  [class.border-emerald-500]="prepaidMethod() === opt.value"
                  [class.bg-emerald-50]="prepaidMethod() === opt.value"
                  [class.text-emerald-700]="prepaidMethod() === opt.value"
                  [class.dark:bg-emerald-900/30]="prepaidMethod() === opt.value"
                  [class.dark:text-emerald-300]="prepaidMethod() === opt.value"
                  [class.border-gray-200]="prepaidMethod() !== opt.value"
                  [class.dark:border-gray-700]="prepaidMethod() !== opt.value"
                  [class.text-gray-600]="prepaidMethod() !== opt.value"
                  [class.dark:text-gray-400]="prepaidMethod() !== opt.value"
                >
                  {{ opt.label | translate }}
                </button>
              }
            </div>
          </div>
        }
        <div class="relative">
          <label
            for=""
            class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block"
          >
            {{ 'SALES.PAYMENT.CUSTOMER_PHONE' | translate }}
          </label>
          <div class="flex gap-1.5">
            <!-- Country code selector -->
            <select
              [ngModel]="selectedCountryCode()"
              (ngModelChange)="selectedCountryCode.set($event)"
              class="h-10 px-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:border-emerald-500 cursor-pointer flex-shrink-0"
            >
              @for (c of COUNTRY_CODES; track c.label) {
                <option [value]="c.code">{{ c.flag }} {{ c.code }}</option>
              }
            </select>
            <!-- Phone number -->
            <input
              pInputText
              type="tel"
              [ngModel]="debtCustomerPhone()"
              (ngModelChange)="debtCustomerPhone.set($event); onCustomerFieldChange($event)"
              (keypress)="onlyNumbers($event)"
              class="flex-1 min-w-0"
              placeholder="XX XXX XX XX"
            />
          </div>
          <!-- Suggestion List -->
          @if (matchingCustomers().length > 0) {
            <div
              class="absolute left-0 right-0 z-50 mt-1 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-48 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700"
            >
              @for (cust of matchingCustomers(); track cust.id) {
                <button
                  type="button"
                  (click)="selectCustomer(cust)"
                  class="w-full text-left px-4 py-2.5 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors flex flex-col gap-0.5"
                >
                  <span class="text-sm font-bold text-gray-900 dark:text-gray-100">{{
                    cust.fullName
                  }}</span>
                  <span class="text-xs text-gray-500 dark:text-gray-400 font-mono">{{
                    cust.phoneNumber
                  }}</span>
                </button>
              }
            </div>
          }
          <!-- Preview of full number -->
          @if (debtCustomerPhone().trim()) {
            <p class="mt-1 text-xs text-gray-400">
              {{ selectedCountryCode() }}{{ debtCustomerPhone().trim() }}
            </p>
          }
        </div>
        <div>
          <label
            for=""
            class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block"
          >
            {{ 'SALES.PAYMENT.CUSTOMER_NAME' | translate }}
          </label>
          <input
            pInputText
            type="text"
            [ngModel]="debtCustomerName()"
            (ngModelChange)="debtCustomerName.set($event); onCustomerFieldChange($event)"
            class="w-full"
            [placeholder]="'SALES.PAYMENT.CUSTOMER_NAME_PH' | translate"
          />
        </div>
        <!-- Qarz to'lov muddati (ixtiyoriy) -->
        <div>
          <label
            for=""
            class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block"
          >
            {{ 'SALES.PAYMENT.DUE_DATE' | translate }}
          </label>
          <p-datepicker
            [ngModel]="debtDueDate()"
            (ngModelChange)="debtDueDate.set($event)"
            dateFormat="dd.mm.yy"
            [showClear]="true"
            [minDate]="today"
            appendTo="body"
            styleClass="w-full"
            [placeholder]="'SALES.PAYMENT.DUE_DATE' | translate"
          />
          <p class="mt-1 text-xs text-gray-400">{{ 'SALES.PAYMENT.DUE_DATE_HINT' | translate }}</p>
        </div>
      </div>
    }
    <!--
      Naqd va kartada xaridor ismi IXTIYORIY — kiritilsa chekda va buyurtma
      hisobotida chiqadi. Nasiyada bu maydon yuqorida, telefon bilan birga majburiy,
      shuning uchun bu yerda takrorlanmaydi.
    -->
    @if (paymentType() !== 2) {
      <div>
        <label
          for=""
          class="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block"
        >
          {{ 'SALES.PAYMENT.CUSTOMER_NAME' | translate }}
          <span class="normal-case font-normal text-gray-400">
            ({{ 'SALES.PAYMENT.OPTIONAL' | translate }})
          </span>
        </label>
        <input
          pInputText
          type="text"
          [ngModel]="debtCustomerName()"
          (ngModelChange)="debtCustomerName.set($event)"
          class="w-full"
          [placeholder]="'SALES.PAYMENT.CUSTOMER_NAME_PH' | translate"
        />
      </div>
    }
  </div>

  <ng-template pTemplate="footer">
    <div class="flex gap-2 justify-end">
      <p-button
        [label]="'COMMON.CANCEL' | translate"
        severity="secondary"
        (onClick)="paymentVisible.set(false)"
      />
      <p-button
        [label]="'SALES.PAYMENT.CONFIRM' | translate"
        [loading]="paymentSubmitting()"
        [disabled]="!isPaymentValid()"
        (onClick)="confirmPayment()"
      />
    </div>
  </ng-template>
</p-dialog>

<!-- ═══ Receipt dialog ══════════════════════════════════════════════════════ -->
<p-dialog
  [visible]="receiptVisible()"
  (visibleChange)="receiptVisible.set($event)"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [appendTo]="'body'"
  styleClass="w-full max-w-sm receipt-print-dialog"
  [header]="'SALES.RECEIPT.TITLE' | translate"
>
  <!--
    Chek belgilanishi `app-receipt` da — orders va debtors ham SHU komponentni
    ishlatadi. Bu yerda nusxa saqlanmaydi: ilgari shunday bo'lgan va valyuta
    o'zgarishlari ikki joyda alohida tuzatilishi kerak edi.
  -->
  @if (receipt(); as r) {
    <app-receipt [receipt]="r" />
  }

  <!-- footer OUTSIDE @if — always in DOM for PrimeNG ContentChildren -->
  <ng-template pTemplate="footer">
    <!--
      "Yopish" ATAYLAB oxirida, "Chop etish" yonida: oyna tor (`max-w-sm`) va to'rt
      tugma qatorga sig'may o'ralib ketadi. Birinchi turganda u yuqoridagi qatorda
      qolib, printdan ajralib qolardi.
    -->
    <div class="flex flex-wrap gap-2 justify-end">
      <p-button
        [label]="'SALES.RECEIPT.PDF' | translate"
        severity="secondary"
        [outlined]="true"
        [loading]="exportBusy()"
        [disabled]="!receiptOrderId()"
        (onClick)="downloadReceiptPdf()"
      >
        <ng-template pTemplate="icon">
          <lucide-icon [img]="FileText" size="14" class="mr-1.5" />
        </ng-template>
      </p-button>
      <p-button
        [label]="'SALES.RECEIPT.SHARE' | translate"
        severity="secondary"
        [outlined]="true"
        [loading]="shareBusy()"
        [disabled]="!receiptOrderId()"
        (onClick)="shareReceipt()"
      >
        <ng-template pTemplate="icon">
          <lucide-icon [img]="Share2" size="14" class="mr-1.5" />
        </ng-template>
      </p-button>
      <p-button [label]="'SALES.RECEIPT.PRINT' | translate" (onClick)="printReceipt()">
        <ng-template pTemplate="icon">
          <lucide-icon [img]="Printer" size="14" class="mr-1.5" />
        </ng-template>
      </p-button>
      <p-button
        [label]="'COMMON.CLOSE' | translate"
        severity="secondary"
        (onClick)="receiptVisible.set(false)"
      />
    </div>
  </ng-template>
</p-dialog>

<!-- ═══ Mobile cart sheet ════════════════════════════════════════════════════ -->
@if (cartSheetOpen()) {
  <!-- eslint-disable-next-line -->
  <div class="fixed inset-0 z-40 bg-black/40 lg:hidden" (click)="cartSheetOpen.set(false)"></div>
  <div
    class="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white dark:bg-gray-900 rounded-t-2xl shadow-2xl max-h-[82vh] flex flex-col"
  >
    <div class="flex justify-center pt-3 pb-1 flex-shrink-0">
      <div class="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600"></div>
    </div>
    <div
      class="px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between flex-shrink-0"
    >
      <div class="flex items-center gap-2">
        <lucide-icon [img]="ShoppingCart" size="17" class="text-emerald-600" />
        <span class="font-bold text-sm text-gray-900 dark:text-gray-100">{{
          'SALES.CART' | translate
        }}</span>
        @if (cartItemCount() > 0) {
          <span
            class="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"
            >{{ cartItemCount() }}</span
          >
        }
      </div>
      <div class="flex items-center gap-1">
        @if (cart().length > 0) {
          <button
            type="button"
            (click)="clearCart()"
            class="p-1.5 text-gray-400 hover:text-rose-500 transition-colors"
          >
            <lucide-icon [img]="Trash2" size="15" />
          </button>
        }
        <button type="button" (click)="cartSheetOpen.set(false)" class="p-1.5 text-gray-400">
          <lucide-icon [img]="X" size="17" />
        </button>
      </div>
    </div>
    <div class="flex-1 overflow-y-auto" style="scrollbar-width: none">
      @if (cart().length === 0) {
        <div class="flex flex-col items-center justify-center py-10 text-gray-400 gap-2">
          <lucide-icon [img]="ShoppingCart" size="36" class="text-gray-200 dark:text-gray-700" />
          <p class="text-xs">{{ 'SALES.CART_EMPTY' | translate }}</p>
        </div>
      } @else {
        <div class="p-2 space-y-1.5">
          @for (item of cart(); track lineId(item)) {
            <div
              class="bg-gray-50 dark:bg-gray-800/60 rounded-xl px-3 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <div class="flex items-center gap-2">
                <!-- LEFT: nom + variant + formula + chegirma badge -->
                <button
                  type="button"
                  (click)="openQtyEdit(item, $event)"
                  class="flex-1 min-w-0 text-left"
                >
                  <p
                    class="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight truncate"
                  >
                    {{ item.productName }}
                  </p>
                  @if (item.variantLabel) {
                    <p class="text-xs text-indigo-500 dark:text-indigo-400 truncate">
                      {{ item.variantLabel }}
                    </p>
                  }
                  <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span class="text-xs text-gray-500 tabular-nums">
                      {{ item.quantity | number: '1.0-6' }} ×
                      {{ item.unitPrice - item.discount | money: item.currency }}
                    </span>
                    @if (item.discount > 0) {
                      <span
                        class="inline-flex items-center text-[10px] bg-rose-100 dark:bg-rose-900\/30 text-rose-500 dark:text-rose-400 px-1.5 py-0.5 rounded-full font-semibold"
                      >
                        @if (item.discountPct !== null) {
                          -{{ item.discountPct }}%
                        } @else {
                          -{{ item.discount | money: item.currency }}
                        }
                      </span>
                    }
                  </div>
                </button>
                <!-- RIGHT: qty (yuqori) + jami (quyi) -->
                <div class="flex flex-col items-end gap-0.5 flex-shrink-0">
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      (click)="decreaseQty(lineId(item))"
                      class="w-8 h-8 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-rose-100 hover:text-rose-600 transition-colors"
                    >
                      <lucide-icon [img]="Minus" size="13" />
                    </button>
                    <button
                      type="button"
                      (click)="openQtyEdit(item, $event)"
                      class="min-w-[2rem] text-center text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      {{ item.quantity | number: '1.0-6' }}
                    </button>
                    <button
                      type="button"
                      (click)="increaseQty(lineId(item))"
                      [disabled]="item.quantity >= item.stocks"
                      class="w-8 h-8 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-emerald-100 hover:text-emerald-600 transition-colors disabled:opacity-40"
                    >
                      <lucide-icon [img]="Plus" size="13" />
                    </button>
                  </div>
                  <span class="text-sm font-bold text-gray-800 dark:text-gray-200 tabular-nums">{{
                    itemTotal(item) | money: item.currency
                  }}</span>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>
    <div class="border-t border-gray-100 dark:border-gray-800 p-3 space-y-2 flex-shrink-0">
      <ng-container *ngTemplateOutlet="cartTotals" />
      <ng-container *ngTemplateOutlet="payButton" />
    </div>
  </div>
}

<!-- ═══ Main POS layout ══════════════════════════════════════════════════════ -->
<div class="flex gap-4 h-[calc(100dvh-9.5rem)] sm:h-[calc(100dvh-8.5rem)] lg:h-[calc(100dvh-8rem)]">
  <!-- Left: Products -->
  <div
    class="flex flex-col flex-1 min-w-0 bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden"
  >
    <!-- Search + store filter + scan -->
    <div class="p-3 border-b border-gray-100 dark:border-gray-800 flex gap-2 items-center">
      <div class="relative flex-1 min-w-0">
        <input
          #searchInput
          pInputText
          type="text"
          class="w-full h-10 pl-9 pr-3 py-2 text-sm"
          [placeholder]="'SALES.SEARCH_PLACEHOLDER' | translate"
          [value]="searchText()"
          (input)="onSearch($any($event.target).value)"
          (keydown.enter)="onSearchEnter()"
        />
      </div>
      <!--
        Telefonda `store-select` sinfi (styles.css) nomni yashirib, buni yonidagi
        skaner tugmasidek kvadrat ikonkaga aylantiradi — 11rem lik tanlagich qidiruv
        maydonidan joy yeb qo'yardi. Bosilganda odatdagi dropdown ochiladi.
      -->
      <!--
        Bu tanlagich boshqa ekranlardagi kabi YASHIRILMAYDI, hatto do'kon bitta
        bo'lsa ham. Kassa ekranida u tanlash vositasidan tashqari KO'RSATKICH:
        kassir har chek oldidan qaysi do'kon nomidan sotayotganini ko'rib turadi.
        Yashirilganda savdo qayerga yozilayotgani ekranda umuman qolmasdi.
      -->
      <p-select
        [options]="storeCtx.stores()"
        optionLabel="name"
        optionValue="id"
        [ngModel]="storeCtx.selectedStoreId()"
        (onChange)="onStoreChange($event.value)"
        [placeholder]="'SALES.STORE_FILTER' | translate"
        appendTo="body"
        styleClass="text-sm flex-shrink-0 h-10 store-select"
        [style]="{ width: '11rem' }"
      >
        <ng-template pTemplate="dropdownicon">
          <i class="pi pi-shop text-sm sm:hidden"></i>
          <i class="pi pi-chevron-down text-xs hidden sm:inline"></i>
        </ng-template>
      </p-select>
      <button
        type="button"
        (click)="scanner.startScan()"
        class="w-10 h-10 rounded-md border border-gray-300 dark:border-gray-700 flex items-center justify-center text-gray-500 hover:text-emerald-600 hover:border-emerald-400 transition-colors flex-shrink-0"
        [pTooltip]="'SALES.SCAN_BARCODE' | translate"
        tooltipPosition="bottom"
      >
        <lucide-icon [img]="Camera" size="17" />
      </button>
    </div>

    <!-- Category pills + statik qidiruv -->
    <div
      class="flex items-center gap-2 px-3 py-2 overflow-hidden border-b border-gray-100 dark:border-gray-800 flex-shrink-0"
    >
      <!--
        Kategoriya qidiruvi. Telefonda input o'rniga ikonka: kategoriya pill'lari
        allaqachon gorizontal siljiydi, doimiy input esa ularga qolgan joyni yeb
        qo'yardi. sm dan yuqorida input doim ochiq.
      -->
      @if (!categorySearchOpen()) {
        <button
          type="button"
          (click)="openCategorySearch(catSearchInput)"
          class="sm:hidden w-7 h-7 flex-shrink-0 rounded-full border border-gray-200 dark:border-gray-700 flex items-center justify-center text-gray-500 hover:text-emerald-600 hover:border-emerald-400 transition-colors"
          [attr.aria-label]="'COMMON.SEARCH' | translate"
        >
          <lucide-icon [img]="Search" size="14" />
        </button>
      }
      <div class="relative flex-shrink-0 sm:block" [class.hidden]="!categorySearchOpen()">
        <i
          class="pi pi-search absolute left-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400"
        ></i>
        <input
          #catSearchInput
          type="text"
          [value]="categorySearch()"
          (input)="categorySearch.set($any($event.target).value)"
          (blur)="closeCategorySearchIfEmpty()"
          [placeholder]="'COMMON.SEARCH' | translate"
          class="w-28 sm:w-32 pl-7 pr-2 py-1 text-xs rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:border-emerald-400"
        />
      </div>

      <div
        class="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <button
          type="button"
          (click)="selectCategory(null)"
          class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0"
          [class.bg-emerald-500]="selectedCategoryId() === null"
          [class.text-white]="selectedCategoryId() === null"
          [class.bg-gray-100]="selectedCategoryId() !== null"
          [class.dark:bg-gray-800]="selectedCategoryId() !== null"
          [class.text-gray-600]="selectedCategoryId() !== null"
          [class.dark:text-gray-400]="selectedCategoryId() !== null"
        >
          {{ 'COMMON.ALL' | translate }}
        </button>
        @for (cat of filteredCategories(); track cat.id) {
          <button
            type="button"
            (click)="selectCategory(cat.id)"
            class="px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all flex-shrink-0"
            [class.bg-emerald-500]="selectedCategoryId() === cat.id"
            [class.text-white]="selectedCategoryId() === cat.id"
            [class.bg-gray-100]="selectedCategoryId() !== cat.id"
            [class.dark:bg-gray-800]="selectedCategoryId() !== cat.id"
            [class.text-gray-600]="selectedCategoryId() !== cat.id"
            [class.dark:text-gray-400]="selectedCategoryId() !== cat.id"
          >
            {{ cat.name }}
          </button>
        }
      </div>

      @if (categoryLoading()) {
        <span
          class="h-4 w-4 flex-shrink-0 rounded-full border-2 border-emerald-500 border-t-transparent animate-spin"
          [attr.aria-label]="'SALES.LOADING_CATEGORIES' | translate"
        ></span>
      }
    </div>

    <!-- Product grid -->
    <div class="flex-1 overflow-y-auto p-3">
      @if (productsLoading() && products().length === 0) {
        <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
          @for (_ of [1, 2, 3, 4, 5, 6, 7, 8]; track $index) {
            <div class="rounded-xl h-32 animate-pulse bg-gray-100 dark:bg-gray-800"></div>
          }
        </div>
      } @else if (products().length === 0) {
        <div class="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
          <lucide-icon [img]="Package" size="40" class="text-gray-300 dark:text-gray-600" />
          <p class="text-sm">{{ 'SALES.NO_PRODUCTS' | translate }}</p>
        </div>
      } @else {
        <div class="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
          @for (product of products(); track product.id) {
            <button
              type="button"
              (click)="openDetail(product)"
              [disabled]="product.stocks <= 0"
              class="group relative flex flex-col rounded-xl border text-left transition-all duration-150 overflow-hidden active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
              [class.border-emerald-400]="cart().some((i) => i.productId === product.id)"
              [class.bg-emerald-50]="cart().some((i) => i.productId === product.id)"
              [class.dark:bg-emerald-900/10]="cart().some((i) => i.productId === product.id)"
              [class.shadow-sm]="cart().some((i) => i.productId === product.id)"
              [class.border-gray-100]="!cart().some((i) => i.productId === product.id)"
              [class.dark:border-gray-800]="!cart().some((i) => i.productId === product.id)"
              [class.bg-gray-50]="!cart().some((i) => i.productId === product.id)"
              [class.dark:bg-gray-800/50]="!cart().some((i) => i.productId === product.id)"
              [class.hover:border-emerald-400]="!cart().some((i) => i.productId === product.id)"
              [class.hover:shadow-md]="!cart().some((i) => i.productId === product.id)"
            >
              @if (cart().find((i) => i.productId === product.id); as ci) {
                <span
                  class="absolute top-1 left-1 z-10 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  >×{{ ci.quantity }}</span
                >
              }
              @if (allowProductImages()) {
                <div
                  class="h-28 sm:h-32 w-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center overflow-hidden"
                >
                  @if (product.imageUrl) {
                    <img
                      [src]="product.imageUrl"
                      [alt]="product.name"
                      class="h-full w-full object-cover"
                    />
                  } @else {
                    <lucide-icon
                      [img]="Package"
                      size="32"
                      class="text-gray-300 dark:text-gray-600"
                    />
                  }
                </div>
              }
              <div class="p-2 flex-1 flex flex-col gap-0.5">
                <p
                  class="text-[11px] sm:text-xs font-semibold text-gray-800 dark:text-gray-200 leading-tight line-clamp-2"
                >
                  {{ product.name }}
                </p>
                <p
                  class="text-xs font-bold text-emerald-600 dark:text-emerald-400 tabular-nums mt-auto"
                >
                  {{ product.sellPrice | money: product.sellCurrency }}
                </p>
                <p class="text-[10px] text-gray-400">
                  {{ 'SALES.STOCK' | translate }}: {{ product.stocks }}
                </p>
              </div>
              @if (product.discount > 0) {
                <span
                  class="absolute top-1 right-1 bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  >-{{ product.discount }}%</span
                >
              }
            </button>
          }
        </div>
        @if (hasMore()) {
          <div class="mt-4 flex justify-center">
            <p-button
              [label]="'SALES.LOAD_MORE' | translate"
              severity="secondary"
              size="small"
              [loading]="productsLoading()"
              (onClick)="loadMore()"
            />
          </div>
        }
      }
    </div>
  </div>

  <!-- Right: Cart (desktop) -->
  <div
    class="hidden lg:flex w-80 xl:w-96 flex-col bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden flex-shrink-0"
  >
    <div
      class="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between"
    >
      <div class="flex items-center gap-2">
        <lucide-icon [img]="ShoppingCart" size="17" class="text-emerald-600" />
        <span class="font-bold text-sm text-gray-900 dark:text-gray-100">{{
          'SALES.CART' | translate
        }}</span>
        @if (cartItemCount() > 0) {
          <span
            class="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none"
            >{{ cartItemCount() }}</span
          >
        }
      </div>
      @if (cart().length > 0) {
        <button
          type="button"
          (click)="clearCart()"
          class="text-gray-400 hover:text-rose-500 transition-colors"
          [pTooltip]="'SALES.CLEAR_CART' | translate"
          tooltipPosition="bottom"
        >
          <lucide-icon [img]="Trash2" size="15" />
        </button>
      }
    </div>
    <div class="flex-1 overflow-y-auto" style="scrollbar-width: none">
      @if (cart().length === 0) {
        <div class="flex flex-col items-center justify-center h-full gap-2 text-gray-400 py-8">
          <lucide-icon [img]="ShoppingCart" size="36" class="text-gray-200 dark:text-gray-700" />
          <p class="text-xs text-center px-4">{{ 'SALES.CART_EMPTY' | translate }}</p>
        </div>
      } @else {
        <div class="p-2 space-y-1.5">
          @for (item of cart(); track lineId(item)) {
            <div
              class="bg-gray-50 dark:bg-gray-800/60 rounded-xl px-3 py-2.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <div class="flex items-center gap-2">
                <!-- LEFT: nom + variant + formula + chegirma badge -->
                <button
                  type="button"
                  (click)="openQtyEdit(item, $event)"
                  class="flex-1 min-w-0 text-left"
                >
                  <p
                    class="text-sm font-semibold text-gray-800 dark:text-gray-200 leading-tight truncate"
                  >
                    {{ item.productName }}
                  </p>
                  @if (item.variantLabel) {
                    <p class="text-xs text-indigo-500 dark:text-indigo-400 truncate">
                      {{ item.variantLabel }}
                    </p>
                  }
                  <div class="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span class="text-xs text-gray-500 tabular-nums">
                      {{ item.quantity | number: '1.0-6' }} ×
                      {{ item.unitPrice - item.discount | money: item.currency }}
                    </span>
                    @if (item.discount > 0) {
                      <span
                        class="inline-flex items-center text-[10px] bg-rose-100 dark:bg-rose-900\/30 text-rose-500 dark:text-rose-400 px-1.5 py-0.5 rounded-full font-semibold"
                      >
                        @if (item.discountPct !== null) {
                          -{{ item.discountPct }}%
                        } @else {
                          -{{ item.discount | money: item.currency }}
                        }
                      </span>
                    }
                  </div>
                </button>
                <!-- RIGHT: qty (yuqori) + jami (quyi) -->
                <div class="flex flex-col items-end gap-0.5 flex-shrink-0">
                  <div class="flex items-center gap-1">
                    <button
                      type="button"
                      (click)="decreaseQty(lineId(item))"
                      class="w-8 h-8 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-rose-100 hover:text-rose-600 transition-colors"
                    >
                      <lucide-icon [img]="Minus" size="13" />
                    </button>
                    <button
                      type="button"
                      (click)="openQtyEdit(item, $event)"
                      class="min-w-[2rem] text-center text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400 hover:underline"
                    >
                      {{ item.quantity | number: '1.0-6' }}
                    </button>
                    <button
                      type="button"
                      (click)="increaseQty(lineId(item))"
                      [disabled]="item.quantity >= item.stocks"
                      class="w-8 h-8 rounded-xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-emerald-100 hover:text-emerald-600 transition-colors disabled:opacity-40"
                    >
                      <lucide-icon [img]="Plus" size="13" />
                    </button>
                  </div>
                  <span class="text-sm font-bold text-gray-800 dark:text-gray-200 tabular-nums">{{
                    itemTotal(item) | money: item.currency
                  }}</span>
                </div>
              </div>
            </div>
          }
        </div>
      }
    </div>
    <div class="border-t border-gray-100 dark:border-gray-800 p-3 space-y-2">
      <ng-container *ngTemplateOutlet="cartTotals" />
      <ng-container *ngTemplateOutlet="payButton" />
    </div>
  </div>
</div>

<!-- Mobile sticky cart bar -->
<div class="fixed bottom-16 lg:hidden left-0 right-0 px-4 z-30 pointer-events-none">
  <div class="pointer-events-auto">
    @if (cart().length > 0) {
      <button
        type="button"
        (click)="cartSheetOpen.set(true)"
        class="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-emerald-600 text-white shadow-xl shadow-emerald-900/30 active:scale-95 transition"
      >
        <div class="flex items-center gap-2">
          <span
            class="bg-white/20 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold"
            >{{ cartItemCount() }}</span
          >
          <span class="text-sm font-semibold">{{ 'SALES.CART' | translate }}</span>
        </div>
        <div class="flex items-center gap-2">
          <!-- Aralash savatda valyutalarni QO'SHIB bo'lmaydi — yonma-yon chiqadi. -->
          <span class="text-sm font-bold tabular-nums">
            @for (g of cartTotalsByCurrency(); track g.currency) {
              @if (!$first) {
                <span class="opacity-60 mx-1">·</span>
              }
              {{ g.total | money: g.currency }}
            }
          </span>
          <lucide-icon [img]="ChevronUp" size="16" />
        </div>
      </button>
    }
  </div>
</div>

<!-- ═══ Shared templates ══════════════════════════════════════════════════════ -->

<!-- Cart totals -->
<ng-template #cartTotals>
  <div class="space-y-1">
    <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400">
      <span>{{ 'SALES.SUBTOTAL' | translate }}</span>
      <span class="tabular-nums">
        @for (g of cartTotalsByCurrency(); track g.currency) {
          <span class="block">{{ g.subtotal | money: g.currency }}</span>
        }
      </span>
    </div>
    @if (cartDiscount() > 0) {
      <div class="flex justify-between text-xs text-rose-500">
        <span>{{ 'SALES.DISCOUNT' | translate }}</span>
        <span class="tabular-nums"
          >-
          @for (g of cartTotalsByCurrency(); track g.currency) {
            @if (g.discount > 0) {
              <span class="block">{{ g.discount | money: g.currency }}</span>
            }
          }
        </span>
      </div>
    }
    <div
      class="flex justify-between text-sm font-bold text-gray-900 dark:text-gray-100 pt-1 border-t border-gray-100 dark:border-gray-800"
    >
      <span>{{ 'SALES.TOTAL' | translate }}</span>
      <span class="tabular-nums">
        @for (g of cartTotalsByCurrency(); track g.currency) {
          <span class="block">{{ g.total | money: g.currency }}</span>
        }
      </span>
    </div>
  </div>
</ng-template>

<!-- Pay button -->
<ng-template #payButton>
  @if (!storeCtx.selectedStoreId()) {
    <div class="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-xs">
      <lucide-icon [img]="AlertCircle" size="13" />
      <span>{{ 'SALES.NO_STORE' | translate }}</span>
    </div>
  }
  <button
    type="button"
    (click)="openPayment()"
    [disabled]="!cart().length || !storeCtx.selectedStoreId()"
    class="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-bold text-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {{ 'SALES.PAY' | translate }}
    @if (cartTotal() > 0) {
      —
      @for (g of cartTotalsByCurrency(); track g.currency) {
        <span class="block">{{ g.total | money: g.currency }}</span>
      }
    }
  </button>
</ng-template>
